import { calculateLanguagePercentage } from "@lib/helper"
import * as m from "@paraglide/messages"
import type { WorkPageData } from "@site-types/publicPageData"
import type {
  SupportedLocale as Lang,
  WorkMarketingItem,
  WorkProductItem,
  WorkTagsItem,
} from "@site-types/site"
import {
  type GalleryImage,
  imageAlt,
  imageDimensions,
  imageTitle,
  imageUrl,
  localized,
  localizedSettings,
  tagsFor,
} from "../componentUtils"
import Tag from "../Tag"
import EsportsPlayer from "./EsportsPlayer"
import GridGallery from "./GridGallery"
import ScrollGallery from "./ScrollGallery"
import WorkSection from "./WorkSection"
import "./styles.css"

export function WorkDetails({ data, locale }: { data: WorkPageData; locale: Lang }) {
  const settings = localizedSettings(data, locale)
  const marketing = data.workMarketing
  const products = localized<WorkProductItem>(data.workProducts, locale)
  const [workTags] = localized<WorkTagsItem>(data.workTags ?? [], locale)
  const workPageVideoUrl = settings.workPageVideoUrl as string | undefined
  const fallbackWorkPageVideoTags = ((settings.workPageVideoTags as string[] | undefined) ?? []).filter(
    (tag) => {
      const { englishPercentage, japanesePercentage } = calculateLanguagePercentage(tag)
      if (locale === "en" && japanesePercentage > 75) return false
      if (locale === "ja" && englishPercentage > 75) return false
      return true
    }
  )
  const workPageVideoTags = dashboardTagsOrFallback(workTags?.esportsBroadcastTags, fallbackWorkPageVideoTags)
  const marketingTags = dashboardTagsOrFallback(
    workTags?.gameCultureMarketingTags,
    tagsFor(
      marketing.map((item) => item.coverImage),
      locale
    )
  )
  const productTags = dashboardTagsOrFallback(
    workTags?.productTags,
    tagsFor(
      products.map((item) => item.coverImage),
      locale,
      {
        filterByLanguage: false,
      }
    )
  )
  const marketingGalleryItems = marketing.map((value, key) => ({
    id: value.id ?? `gallery-item-${key}`,
    label: String(value.title ?? imageAlt(value.coverImage, locale) ?? `Marketing project ${key + 1}`),
    coverSrc: value.coverImage ? imageUrl(value.coverImage, "workCover") || "" : "",
    coverAlt: imageAlt(value.coverImage, locale),
    coverTitle: imageTitle(value.coverImage, locale),
    coverDimensions: imageDimensions(value.coverImage),
    images: ((value.gallery as GalleryImage[] | undefined) ?? []).filter(Boolean).map((galleryImg) => ({
      src: imageUrl(galleryImg, "workModal") || "",
      alt: imageAlt(galleryImg, locale),
      title: imageTitle(galleryImg, locale),
      dimensions: imageDimensions(galleryImg),
    })),
  }))

  return (
    <>
      <WorkSection
        className="esports"
        title={m.work_heading_esports({}, { locale: locale })}
      >
        <EsportsPlayer videoUrl={workPageVideoUrl} />
        <Tag data={workPageVideoTags} />
      </WorkSection>
      <WorkSection
        className="marketing"
        title={m.work_heading_marketing({}, { locale: locale })}
      >
        <ScrollGallery items={marketingGalleryItems} />
        <Tag data={marketingTags} />
      </WorkSection>
      <WorkSection
        className="products"
        title={m.work_heading_products({}, { locale: locale })}
      >
        <GridGallery
          products={products}
          locale={locale}
        />
        <Tag data={productTags} />
      </WorkSection>
    </>
  )
}

function dashboardTagsOrFallback(tags: string[] | undefined, fallback: string[]) {
  return Array.isArray(tags) ? tags : fallback
}

export default WorkDetails
